import { useController } from 'react-hook-form';
import type { Control, FieldValues, FieldPath, ControllerRenderProps } from 'react-hook-form';
import { Select } from '@schibsted-svp/react-ui';
import type { ExtendedProps as SelectProps, DefaultOptionType } from '@schibsted-svp/react-ui/lib/es/Select';
import { findSingleOption, getValueFromOption } from 'components/ui/Form/Select';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFetchSeasonsData } from '../../categories/Form/SeasonsForm/hooks';
import { seriesFetch } from '../../../store/series/actions';

type SeasonSelectProps<TFieldValues extends FieldValues> = {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    provider: string;
    categoryId: number;
    normalizeValue?: boolean;
} & SelectProps<DefaultOptionType>;

export function SeasonSelect<TFieldValues extends FieldValues>({
    name,
    control,
    provider,
    categoryId,
    normalizeValue = true,
    ...rest
}: SeasonSelectProps<TFieldValues>) {
    const { field, fieldState } = useController({ name, control });
    const dispatch = useDispatch();
    const { seasons, isSeries } = useFetchSeasonsData({ categoryId, provider });

    useEffect(() => {
        if (isSeries) {
            dispatch(seriesFetch(provider, categoryId));
        }
    }, [categoryId, isSeries, provider, dispatch]);

    const options = seasons.map(({ title, seasonNumber }) => ({
        label: title,
        value: seasonNumber,
    }));

    const normalizeValueHandlers: Pick<ControllerRenderProps, 'value' | 'onChange'> = {
        value: findSingleOption(options)(field.value),
        onChange: (value) => field.onChange(getValueFromOption(value)),
    };

    return (
        <Select
            {...field}
            {...(normalizeValue && normalizeValueHandlers)}
            options={options}
            isClearable
            backspaceRemovesValue={false}
            splitButton={false}
            size="small"
            error={fieldState.error?.message}
            {...rest}
        />
    );
}
